// ============================================================
// V2 platform — tenant context + collection-path helpers.
//
// R1 (2026-04-23): introduces the multi-tenant subcollection
// prefix `/tenants/{tenantId}/<collection>/...`. Every Firestore
// read/write in V2 funnels through these helpers so a future
// migration (per-doc tenantId field, per-tenant project, region
// split) is a one-file change — not a 100-call-site sweep.
//
// Owner: principal-architect + firebase-expert (R1 4-way debate
// recorded in DESIGN_LOG.md).
//
// Default tenant: 'ccy-engineering' — bootstrap value used until
// every user has a tenantId on their /users/{uid} doc and/or
// Auth custom claim. Once R1 backfill + set-tenant script have
// run on all users, this default becomes unreachable in practice
// (kept as last-resort safety so a brand-new sign-in never
// throws "no tenant").
// ============================================================

import {
  collection, doc, query, getDoc, setDoc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { materializeRolesForPack } from './permissions.js';

export const DEFAULT_TENANT_ID = 'ccy-engineering';

// ─────────────────────────────────────────────────────────────
// A3 (2026-04-25) — Subdomain routing.
//
// Subdomain pattern: <subdomain>.erp.ccyengineering.com
//   acme.erp.ccyengineering.com → resolves to tenant where subdomain==='acme'
// Fallback hostnames (no subdomain detection):
//   ccy-erp-v2.web.app          → URL-param flow (current behavior)
//   ccy-erp-v2.firebaseapp.com  → URL-param flow
//   localhost / 127.0.0.1       → URL-param flow (dev)
//
// The pre-auth tenant resolution does NOT read /tenants/{tid} (rules require
// auth). Instead it reads /public-tenant-branding/{subdomain} — a slim,
// world-readable mirror containing ONLY brand fields (name, logo, colors).
// See DESIGN_LOG A3 sub-decisions 2 + 3 for the security debate.
// ─────────────────────────────────────────────────────────────

// Hostnames that should NEVER be parsed as `<sub>.erp.ccyengineering.com`.
const _FALLBACK_HOSTNAMES = new Set([
  'ccy-erp-v2.web.app',
  'ccy-erp-v2.firebaseapp.com',
  'localhost',
  '127.0.0.1',
  '0.0.0.0'
]);

/**
 * Parse the hostname and return the subdomain string (the `<sub>` part of
 * `<sub>.erp.ccyengineering.com`), or null if the host is a fallback /
 * unrecognized pattern.
 *
 *   resolveSubdomainFromHostname('acme.erp.ccyengineering.com') → 'acme'
 *   resolveSubdomainFromHostname('ccy-erp-v2.web.app')          → null
 *   resolveSubdomainFromHostname('localhost')                    → null
 *   resolveSubdomainFromHostname('erp.ccyengineering.com')       → null  (no subdomain — bare host)
 *
 * Defensive: subdomain MUST be lowercase letters/digits/hyphen, 2-63 chars
 * (DNS limit). Reject anything else (defense against header injection).
 */
export function resolveSubdomainFromHostname(hostname) {
  if (!hostname) return null;
  const host = String(hostname).toLowerCase().trim();
  if (_FALLBACK_HOSTNAMES.has(host)) return null;

  // Match exactly `<sub>.erp.ccyengineering.com`.
  const m = host.match(/^([a-z0-9][a-z0-9-]{0,61}[a-z0-9]|[a-z0-9])\.erp\.ccyengineering\.com$/);
  if (!m) return null;

  // Reserved subdomain words — never resolve to a tenant.
  const sub = m[1];
  if (['www', 'mail', 'api', 'admin', 'platform', 'system', 'erp'].includes(sub)) return null;

  return sub;
}

/**
 * Look up the public branding mirror by SUBDOMAIN doc id.
 * Returns the branding object `{ tenantId, name, brandName, tagline,
 * primaryColor, accentColor, logoUrl, faviconUrl }` or null if no tenant
 * is registered for that subdomain.
 *
 * SAFE TO CALL PRE-AUTH — Firestore Rules permit unauthenticated read on
 * `/public-tenant-branding/{subdomain}` (single-doc read only; no list).
 *
 * Caller (main.js bootstrapLoginDOM) uses this to pre-fill the login screen
 * with tenant brand BEFORE the user authenticates.
 */
export async function loadPublicTenantBranding(subdomain) {
  if (!subdomain) return null;
  try {
    const snap = await getDoc(doc(db, 'public-tenant-branding', String(subdomain).toLowerCase()));
    if (!snap.exists()) return null;
    const d = snap.data() || {};
    return {
      subdomain:    snap.id,
      tenantId:     String(d.tenantId    || ''),
      name:         String(d.name        || ''),
      brandName:    String(d.brandName   || d.name || ''),
      tagline:      String(d.tagline     || ''),
      primaryColor: String(d.primaryColor|| ''),
      accentColor:  String(d.accentColor || ''),
      logoUrl:      String(d.logoUrl     || ''),
      faviconUrl:   String(d.faviconUrl  || ''),
      updatedAt:    d.updatedAt          || null
    };
  } catch (e) {
    // Pre-auth read failure is non-fatal — fall back to the generic CCY
    // login screen and let the user authenticate normally.
    console.warn('[loadPublicTenantBranding] read failed:', e.message);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// Tenant context — set ONCE at app boot by main.js after auth.
// Read by every collection helper below. All reads/writes hard-
// fail if no tenant context has been set (defense against bugs
// where a feature module forgets to depend on the platform init).
// ─────────────────────────────────────────────────────────────

let _currentTenantId = null;
let _currentTenantConfig = null;
let _initPromise = null;

/**
 * Set the active tenant for the current session. Idempotent —
 * calling with the same tenantId is a no-op. Calling with a
 * different tenantId throws (V2 doesn't support runtime tenant-
 * switch yet; that's an R5+ feature).
 */
export function setCurrentTenant(tenantId) {
  if (!tenantId) throw new Error('setCurrentTenant: tenantId required');
  if (_currentTenantId && _currentTenantId !== tenantId) {
    throw new Error(
      `setCurrentTenant: cannot switch tenant mid-session ` +
      `(currently ${_currentTenantId}, attempted ${tenantId})`
    );
  }
  _currentTenantId = String(tenantId);
}

/** Get the active tenant id. Throws if not yet set (boot-order bug). */
export function getCurrentTenantId() {
  if (!_currentTenantId) {
    throw new Error(
      'getCurrentTenantId: no tenant context set. ' +
      'main.js must call setCurrentTenant() after auth, ' +
      'before any data layer read/write.'
    );
  }
  return _currentTenantId;
}

/** True if a tenant has been resolved this session. */
export function hasTenantContext() {
  return _currentTenantId !== null;
}

// ─────────────────────────────────────────────────────────────
// Tenant resolution — claim → users-doc → default fallback.
// Mirrors the priority chain documented in DESIGN_LOG R1 boot
// resolution entry (2026-04-23).
// ─────────────────────────────────────────────────────────────

/**
 * Resolve the tenant id for the given Firebase Auth user.
 * Priority: custom claim → /users/{uid}.tenantId → DEFAULT_TENANT_ID.
 *
 * Does NOT call setCurrentTenant — caller (main.js) decides when
 * to commit the value to session state.
 */
export async function getUserTenant(user) {
  if (!user) return DEFAULT_TENANT_ID;

  // 1. Custom claim — fastest, no Firestore read.
  try {
    const tokenResult = await user.getIdTokenResult(/* forceRefresh */ false);
    const claimTenant = tokenResult.claims.tenantId || tokenResult.claims.tenant_id;
    if (claimTenant) return String(claimTenant);
  } catch (e) {
    console.warn('[getUserTenant] claim read failed:', e);
  }

  // 2. /users/{uid}.tenantId fallback (Spark-plan compatible until claims land).
  try {
    const snap = await getDoc(doc(db, 'users', user.uid));
    if (snap.exists()) {
      const t = snap.data().tenantId;
      if (t) return String(t);
    }
  } catch (e) {
    console.warn('[getUserTenant] doc fallback failed:', e);
  }

  // 3. Default — bootstrap path for first sign-in before any backfill ran.
  return DEFAULT_TENANT_ID;
}

// ─────────────────────────────────────────────────────────────
// Tenant config doc — /tenants/{tenantId}.
// Cached in sessionStorage to avoid re-reading on every tab open.
// ─────────────────────────────────────────────────────────────

const _CONFIG_CACHE_KEY = '_v2_tenant_config_';

/**
 * Load the tenant config doc and cache it. Reads sessionStorage
 * first (subsequent tab opens get an instant config without a
 * Firestore round-trip); falls through to Firestore on cache miss.
 *
 * Returns the config object, or null if the tenant doc doesn't
 * exist (which is a bootstrap state — main.js should fall back
 * to running the V2 app with the default modules enabled).
 *
 * 2026-04-27: also fetches companyInfo/profile (when present) and
 * attaches as `config.companyInfo`. Keeps the cache shape backward
 * compatible — older callers that don't read companyInfo see the
 * same fields they always did.
 */
export async function loadTenantConfig(tenantId) {
  if (!tenantId) throw new Error('loadTenantConfig: tenantId required');

  // sessionStorage cache (per-tab)
  try {
    const cached = sessionStorage.getItem(_CONFIG_CACHE_KEY + tenantId);
    if (cached) {
      const parsed = JSON.parse(cached);
      _currentTenantConfig = parsed;
      return parsed;
    }
  } catch (e) { /* cache read failure is non-fatal */ }

  // Firestore read — tenant doc + companyInfo/profile in parallel.
  // Two reads on cold cache; both cached together for the rest of the session.
  const [snap, companySnap] = await Promise.all([
    getDoc(doc(db, 'tenants', tenantId)),
    getDoc(doc(db, 'tenants', tenantId, 'companyInfo', 'profile')).catch(() => null)
  ]);
  if (!snap.exists()) {
    console.warn(`[loadTenantConfig] tenants/${tenantId} not found — running with bootstrap defaults`);
    _currentTenantConfig = null;
    return null;
  }
  const config = { id: snap.id, ...snap.data() };
  if (companySnap && companySnap.exists()) {
    config.companyInfo = { ...companySnap.data() };
  }
  _currentTenantConfig = config;

  // Persist for future tab opens
  try {
    sessionStorage.setItem(_CONFIG_CACHE_KEY + tenantId, JSON.stringify(config));
  } catch (e) { /* storage quota / private mode — non-fatal */ }

  return config;
}

/** Get the cached tenant config (no I/O). Returns null if not yet loaded. */
export function getCurrentTenantConfig() {
  return _currentTenantConfig;
}

/**
 * Invalidate the sessionStorage cache. Called by Settings UI after
 * an admin edits the tenant config so the next tab open re-fetches.
 */
export function clearTenantConfigCache(tenantId) {
  const tid = tenantId || _currentTenantId;
  if (!tid) return;
  try { sessionStorage.removeItem(_CONFIG_CACHE_KEY + tid); }
  catch (e) { /* non-fatal */ }
}

/**
 * Update the enabledModules array on a tenant config doc. Used by the
 * Settings → Platform Modules UI. Director/admin only — Firestore Rules
 * additionally enforce `isPlatformAdmin()` server-side.
 *
 * Side-effects:
 *   - writes tenants/{tenantId}.enabledModules + UpdatedAt + UpdatedBy
 *   - invalidates sessionStorage cache so next tab open re-fetches
 *   - in-memory _currentTenantConfig is updated so the running tab sees
 *     the new list immediately (the sidebar / module set are rebuilt
 *     only at next sign-in by design — see ARCHITECTURE.md §3 boot order)
 *
 * @param {string}   tenantId
 * @param {string[]} newList   new canonical enabledModules array
 * @param {object}   user      { email } — for audit trail
 * @returns {Promise<object>}  the updated config snapshot
 */
export async function updateEnabledModules(tenantId, newList, user) {
  if (!tenantId) throw new Error('updateEnabledModules: tenantId required');
  if (!Array.isArray(newList)) throw new Error('updateEnabledModules: newList must be an array');
  // De-dupe defensively while preserving order.
  const dedup = [];
  const seen = new Set();
  for (const id of newList) {
    if (!id || typeof id !== 'string') continue;
    if (seen.has(id)) continue;
    seen.add(id); dedup.push(id);
  }
  await updateDoc(doc(db, 'tenants', tenantId), {
    enabledModules: dedup,
    UpdatedAt:      serverTimestamp(),
    UpdatedBy:      user?.email || 'platform-admin'
  });
  // Refresh local snapshot for the calling tab.
  if (_currentTenantConfig && _currentTenantConfig.id === tenantId) {
    _currentTenantConfig = { ..._currentTenantConfig, enabledModules: dedup };
  }
  // Invalidate sessionStorage so the NEXT tab open re-fetches.
  clearTenantConfigCache(tenantId);
  return _currentTenantConfig || { id: tenantId, enabledModules: dedup };
}

/**
 * R4 (2026-04-24) — Apply an industry pack to the current tenant.
 * Atomic write of:
 *   - enabledModules (replaces with the pack's canonical list)
 *   - industryPack (the pack id reference)
 *   - theme defaults (ONLY if tenant has no theme.primaryColor — never
 *     overwrite a customer-customized brand)
 *
 * Side-effects mirror updateEnabledModules: in-memory cache patched,
 * sessionStorage invalidated. Module set is reloaded only on next sign-in
 * (ARCHITECTURE.md §3 boot order).
 *
 * @param {string} tenantId
 * @param {{
 *   id: string, enabledModules: string[],
 *   defaultTheme: { brandName: string, primaryColor: string, accentColor: string }
 * }} pack  the resolved industry pack object (caller already looked it up)
 * @param {object} user  { email } — for audit trail
 * @returns {Promise<object>}  the updated config snapshot
 */
export async function applyIndustryPack(tenantId, pack, user) {
  if (!tenantId)        throw new Error('applyIndustryPack: tenantId required');
  if (!pack || !pack.id) throw new Error('applyIndustryPack: pack object required');
  const cur = (_currentTenantConfig && _currentTenantConfig.id === tenantId)
    ? _currentTenantConfig : null;

  const update = {
    enabledModules: pack.enabledModules.slice(),
    industryPack:   pack.id,
    UpdatedAt:      serverTimestamp(),
    UpdatedBy:      user?.email || 'platform-admin'
  };

  // Theme defaults only on first-touch — never overwrite custom branding.
  // (Detection: a tenant that's never set a theme typically has
  // theme.primaryColor missing or falsy. Customer-customized themes
  // always have these set by the theming UI in R5.)
  const hasCustomTheme = cur && cur.theme && cur.theme.primaryColor;
  if (!hasCustomTheme) {
    update.theme = {
      ...((cur && cur.theme) || {}),
      brandName:    (cur && cur.theme && cur.theme.brandName) || (cur && cur.name) || pack.defaultTheme.brandName,
      primaryColor: pack.defaultTheme.primaryColor,
      accentColor:  pack.defaultTheme.accentColor
    };
  }

  await updateDoc(doc(db, 'tenants', tenantId), update);

  // R6 (2026-04-24) — materialize the pack's role definitions into
  // tenants/{tid}/roles/{roleId}. INSERT-IF-MISSING by default; existing
  // role docs (admin-customized in R6.5) are preserved. The director can
  // tick "Reset roles to pack defaults" in the Apply-Pack confirm modal
  // to pass `overwriteCustomized: true` (R6.5 — UI deferred).
  try {
    const matResult = await materializeRolesForPack(tenantId, pack, { user });
    console.log('[applyIndustryPack] roles materialized:', matResult);
  } catch (e) {
    // Best-effort: tenant doc update succeeded but role materialization
    // failed. Caller (Settings UI) gets a warning toast; admin can re-run
    // via the future "Reset roles" button or the r6-materialize-roles CLI.
    // The tenant is FUNCTIONAL because director (system role, fast-path
    // in Rules) doesn't need the role doc.
    console.warn('[applyIndustryPack] role materialization failed (tenant doc OK):', e);
  }

  // Patch in-memory cache.
  if (cur) {
    _currentTenantConfig = { ...cur, ...update };
  }
  clearTenantConfigCache(tenantId);
  return _currentTenantConfig || { id: tenantId, ...update };
}

// ─────────────────────────────────────────────────────────────
// Collection-path helpers — THE main API for data-layer files.
// Every Firestore call site uses these instead of literal
// `collection(db, 'inventory')` / `doc(db, 'inventory', id)`.
// ─────────────────────────────────────────────────────────────

/**
 * Get a CollectionReference under the current tenant.
 *   tenantCol('inventory')           → collection(db, 'tenants', <tid>, 'inventory')
 *   tenantCol('stockmovements')      → collection(db, 'tenants', <tid>, 'stockmovements')
 *
 * Throws if no tenant context is set — fail loud so we don't
 * accidentally write to a wrong path.
 */
export function tenantCol(name) {
  if (!name) throw new Error('tenantCol: collection name required');
  const tid = getCurrentTenantId();
  return collection(db, 'tenants', tid, name);
}

/**
 * Get a DocumentReference under the current tenant.
 *   tenantDoc('inventory', 'abc123') → doc(db, 'tenants', <tid>, 'inventory', 'abc123')
 *
 * If `id` is omitted, returns a doc ref with an auto-generated id
 * (mirrors `doc(collection(db, 'inventory'))` pattern from the SDK).
 */
export function tenantDoc(name, id) {
  if (!name) throw new Error('tenantDoc: collection name required');
  const tid = getCurrentTenantId();
  if (id) {
    return doc(db, 'tenants', tid, name, id);
  }
  return doc(collection(db, 'tenants', tid, name));
}

/**
 * Get a sub-collection reference under a tenant doc — used for the
 * tenant config doc itself if we ever add per-tenant settings sub-
 * collections (e.g. `tenants/{tid}/rolePermissions/{role}`).
 *   tenantSubCol('rolePermissions') → collection(db, 'tenants', <tid>, 'rolePermissions')
 *
 * (Functionally identical to tenantCol — kept as alias for readability
 * when the caller wants to signal "this is platform metadata, not domain data".)
 */
export const tenantSubCol = tenantCol;
export const tenantSubDoc = tenantDoc;

// ─────────────────────────────────────────────────────────────
// Storage path helpers — match the Firestore tenant scope so
// rules + paths stay symmetrical.
// ─────────────────────────────────────────────────────────────

/**
 * Build a storage path scoped to the current tenant.
 *   tenantStoragePath('suppliers/SUP-0001/qr/img.png')
 *     → 'tenants/ccy-engineering/suppliers/SUP-0001/qr/img.png'
 *
 * Caller passes the post-tenant portion. We never prepend a slash;
 * Firebase Storage paths are slash-separated but root-less.
 */
export function tenantStoragePath(subPath) {
  if (!subPath) throw new Error('tenantStoragePath: subPath required');
  const tid = getCurrentTenantId();
  const clean = String(subPath).replace(/^\/+/, '');
  return `tenants/${tid}/${clean}`;
}

// ─────────────────────────────────────────────────────────────
// Bootstrap helper for set-tenant.js — also creates the tenant
// config doc if it doesn't exist. Used by the migration scripts.
// (NOT used at runtime — the app NEVER writes to /tenants/.)
// ─────────────────────────────────────────────────────────────

/**
 * Default tenant config seed for CCY. Mirrored by scripts/set-tenant.js
 * so we have a single source of truth for the bootstrap shape.
 */
export const CCY_TENANT_SEED = {
  tenantId:     'ccy-engineering',
  name:         'CCY Engineering',
  industryPack: 'manufacturing',
  enabledModules: [
    // R3 (2026-04-24) — canonical 10-module list. Each entry is a real
    // module with a manifest under src/modules/<id>/manifest.js.
    // Order is dependency-respecting (deps first) as a defensive convention.
    'dashboard',
    'inventory',
    'suppliers',
    'procurement',          // depends on inventory + suppliers
    'sales',                // depends on inventory
    'finance',              // 2026-05-20 — Expenses & Purchases (admin tier only; no inventory dep)
    'hr',
    'leave',                // depends on hr
    'payroll',              // depends on hr
    'account',              // depends on hr + leave + payroll
    'settings'              // depends on hr + leave
  ],
  theme: {
    brandName:    'CCY Engineering',
    primaryColor: '#1a73e8',
    accentColor:  '#34a853',
    logoPath:     '',     // set later when logo upload UI lands (R5)
    faviconPath:  '',
    tagline:      'Engineering. Built right.'
  },
  region:        'asia-southeast1',
  status:        'active',
  CreatedAt:     null,    // serverTimestamp populated by writer
  UpdatedAt:     null,
  CreatedBy:     'R1-platform-bootstrap',
  UpdatedBy:     'R1-platform-bootstrap'
};
